import { z } from 'zod';
import { DateString, JsonObject, Timestamp, Uuid } from './common.js';
import {
  BindingStrength,
  ContentDateBasis,
  FactStatus,
  FactType,
  NegationReason,
  Sensitivity,
  Severity,
  SourceKind,
  SourceTier,
} from './vocab.js';

/**
 * **Proposed addition — not yet blessed into server-design §3.**
 *
 * `GET /orgs/:orgId/subjects/:id/data` — the substrate itself, read-only: every
 * fact lineage on the subject with all of its versions and all of its supporting
 * claims, plus the claims that never became facts. It is the answer to "what is
 * actually behind this line of the brief", one read, with nothing summarised
 * away.
 *
 * It follows the `FACT_TYPE_BRIEF_SECTION` precedent in `vocab.ts`: written here
 * first because the surface that consumes it exists, flagged as proposed until
 * server-design §3.1 carries the route in its own table.
 *
 * Two rules from this package's preamble hold as usual — no score, confidence,
 * rank or percentage appears anywhere, and nothing removed is dropped (negated
 * lineages, discarded claims and superseded versions are all listed, labelled).
 * The third rule of the office-facing schemas does **not** apply here: this is
 * the power view, so raw enum values (`pending_adjudication`, `name_only`) are
 * on the wire deliberately, and the client renders them as they are rather than
 * inventing copy for them.
 */

/**
 * A claim's position in the pipeline (server-design.md §7.2, `claim.resolution_status`).
 * Not in `vocab.ts`: the office-facing contract never exposed it, and this
 * schema is proposed rather than blessed — it keeps its own vocabulary until the
 * route is.
 *
 *  - `resolved` — attached to a lineage;
 *  - `held` — its counterparty would not resolve, so no lineage exists yet (§7.2 step 7);
 *  - `pending_adjudication` — gated blocking; no fact was written and the
 *    resolution is deferred to the answer (§7.2 step 6);
 *  - `discarded` — an answer or a correction took it out of consideration.
 */
export const CLAIM_RESOLUTION_STATUSES = [
  'resolved',
  'held',
  'pending_adjudication',
  'discarded',
] as const;
export const ClaimResolutionStatus = z.enum(CLAIM_RESOLUTION_STATUSES);
export type ClaimResolutionStatus = z.infer<typeof ClaimResolutionStatus>;

/** Which end of a pair or org-context fact this subject sits on (§7.2c). */
export const LINEAGE_SIDES = ['subject', 'counterparty'] as const;
export const LineageSide = z.enum(LINEAGE_SIDES);
export type LineageSide = z.infer<typeof LineageSide>;

/**
 * One claim, at the grain the substrate stores it. Richer than `FactClaimEntry`
 * (the brief's citation drill-down) by exactly the fields the office never needed
 * and an operator reading the machine does: resolution status, binding strength,
 * the facet and run that produced it.
 *
 * `evidence_excerpt` is the stored retrieval's excerpt; a first-party claim has
 * none and cites `prose_input_id` instead. Citations resolve to stored evidence,
 * so `url` stays quiet plain text, never a link target.
 */
export const SubjectDataClaim = z.object({
  claim_id: Uuid,
  source_kind: SourceKind,
  source_tier: SourceTier,
  resolution_status: ClaimResolutionStatus,
  binding_strength: BindingStrength,

  content_date: DateString.nullable(),
  content_date_basis: ContentDateBasis,
  observed_at: Timestamp,

  evidence_excerpt: z.string().nullable(),
  evidence_id: Uuid.nullable(),
  /**
   * What the source itself said, verbatim — for a first-party claim, the
   * sentence out of the prose it was read from. **`null` where the substrate
   * stored none**, and present either way, like the five fields around it: this
   * was the one key on the object spelled as an absence, and "the claim carried
   * no words of its own" and "a server too old to send this key" are not the
   * same statement (see this package's README).
   */
  raw: z.string().nullable(),
  /** First-party claims cite their prose instead of a retrieval. */
  prose_input_id: Uuid.nullable(),
  url: z.string().nullable(),

  /**
   * Attached after the lineage was negated — corroboration that does not
   * resurrect (§6.2). Always `false` on an unattached claim, which has no
   * lineage to have arrived after.
   */
  post_negation: z.boolean(),

  /** The pipeline provenance. Both null for a first-party claim. */
  facet: z.string().nullable(),
  run_id: Uuid.nullable(),
});
export type SubjectDataClaim = z.infer<typeof SubjectDataClaim>;

/**
 * One immutable revision. `fields` is nullable because a shred nulls the payload
 * to the skeleton and keeps the row; `is_current` marks the one the §7.2b
 * precedence rules selected, and every other version listed here is a real
 * competing revision, not history that was overwritten.
 */
export const SubjectDataVersion = z.object({
  version_no: z.number().int().positive(),
  fields: JsonObject.nullable(),
  sensitivity: Sensitivity,
  /** Which catalog schema this version was written against. */
  catalog_version: z.string(),
  created_at: Timestamp,
  /** The claim that produced this revision, and the decision that recorded it. */
  source_claim_id: Uuid.nullable(),
  created_by_decision_id: Uuid.nullable(),
  is_current: z.boolean(),
});
export type SubjectDataVersion = z.infer<typeof SubjectDataVersion>;

/** One lineage: what it is, where it stands, every version, every claim. */
export const SubjectDataLineage = z.object({
  lineage_id: Uuid,
  fact_type: FactType,
  /** `counterparty` when the lineage is keyed to the other end of the pair (§7.2c). */
  side: LineageSide,

  status: FactStatus,
  /** Set only when `status = 'negated'` — removed things stay visible, with the reason. */
  negation_reason: NegationReason.nullable(),

  /**
   * The canonical per-type key the dedup step wrote. Keyless types (`finding`)
   * carry the lineage id itself as the sentinel, which is what makes them
   * unmatchable by key.
   */
  dedup_key: z.string(),
  sensitivity: Sensitivity,
  /** Server-computed off the current version's fields (§7.1). */
  severity: Severity,

  first_recorded_at: Timestamp,
  last_corroborated: DateString.nullable(),

  /** Oldest first; exactly one carries `is_current` unless the lineage was shredded. */
  versions: z.array(SubjectDataVersion),
  /** Every attached claim, oldest attachment first. */
  claims: z.array(SubjectDataClaim),
});
export type SubjectDataLineage = z.infer<typeof SubjectDataLineage>;

/**
 * A claim that is not behind any fact — held, blocking-gated, or discarded.
 * These are the things the brief structurally cannot show, which is why this
 * read exists at all; `fact_type` is the claim's own asserted type, since no
 * lineage carries it.
 */
export const SubjectDataUnattachedClaim = SubjectDataClaim.extend({
  fact_type: FactType,
});
export type SubjectDataUnattachedClaim = z.infer<typeof SubjectDataUnattachedClaim>;

export const SubjectData = z.object({
  subject_id: Uuid,
  /** Both sides unioned in, ordered by fact type then by first record. */
  lineages: z.array(SubjectDataLineage),
  unattached_claims: z.array(SubjectDataUnattachedClaim),
});
export type SubjectData = z.infer<typeof SubjectData>;
